import type { SubAccount } from "@adpilot/shared";
import { IconEdit, IconKey, IconPlus, IconTrash, IconUsers } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChangePasswordDialog } from "@/components/account/change-password-dialog";
import { SUB_ACCOUNTS_QUERY_KEY, SubAccountDrawer } from "@/components/account/sub-account-drawer";
import { PageHeader } from "@/components/patterns/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { deleteSubAccount, fetchSubAccounts, updateAccountProfile } from "@/lib/account-api";
import { getApiErrorMessage } from "@/lib/api-error";
import { useAuthStore } from "@/stores/auth";

export const Route = createFileRoute("/_authed/account")({
  beforeLoad: () => {
    // 仅主账号与管理员可进账号管理;普通子账号直接回首页(后端同样拒绝)。
    const user = useAuthStore.getState().user;
    if (user && !(user.isMainAccount || user.role === "admin")) {
      throw redirect({ to: "/" });
    }
  },
  component: AccountPage,
});

function AccountPage() {
  const { t } = useTranslation("account");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<SubAccount | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const query = useQuery({ queryKey: SUB_ACCOUNTS_QUERY_KEY, queryFn: fetchSubAccounts });

  function openCreate() {
    setEditing(null);
    setDrawerOpen(true);
  }

  function openEdit(subAccount: SubAccount) {
    setEditing(subAccount);
    setDrawerOpen(true);
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={t("title")}
        actions={
          <Button variant="outline" size="sm" onClick={() => setPasswordDialogOpen(true)}>
            <IconKey data-icon="inline-start" />
            {t("changePassword.title")}
          </Button>
        }
      />

      <MainAccountInfoCard />

      <section className="flex flex-col gap-3 rounded-lg border bg-card p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">{t("subAccounts.title")}</h2>
          <Button size="sm" onClick={openCreate}>
            <IconPlus data-icon="inline-start" />
            {t("subAccounts.create")}
          </Button>
        </div>

        {query.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : query.isError ? (
          <ErrorState message={t("subAccounts.loadError")} onRetry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <EmptyState
            icon={<IconUsers className="size-8" />}
            title={t("subAccounts.empty.title")}
            description={t("subAccounts.empty.description")}
            actionLabel={t("subAccounts.empty.cta")}
            onAction={openCreate}
          />
        ) : (
          <SubAccountTable subAccounts={query.data} onEdit={openEdit} />
        )}
      </section>

      <SubAccountDrawer open={drawerOpen} onOpenChange={setDrawerOpen} editing={editing} />
      <ChangePasswordDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen} />
    </div>
  );
}

/** 主账号信息块:邮箱/姓名可编辑,是否主账号只读(Xnurta 账号信息区)。 */
function MainAccountInfoCard() {
  const { t } = useTranslation("account");
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const [editingField, setEditingField] = useState<"name" | "email" | null>(null);
  const [draft, setDraft] = useState("");

  const mutation = useMutation({
    mutationFn: (input: { name?: string; email?: string }) => updateAccountProfile(input),
    onSuccess: (_data, input) => {
      toast.success(t("info.updated"));
      if (user) setUser({ ...user, ...input });
      setEditingField(null);
    },
    onError: async (error) => {
      toast.error(await getApiErrorMessage(error));
    },
  });

  function startEdit(field: "name" | "email") {
    setDraft((field === "name" ? user?.name : user?.email) ?? "");
    setEditingField(field);
  }

  function save() {
    const value = draft.trim();
    if (!value || mutation.isPending) return;
    if (editingField === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.toLowerCase())) {
      toast.error(t("drawer.emailInvalid"));
      return;
    }
    mutation.mutate(editingField === "name" ? { name: value } : { email: value.toLowerCase() });
  }

  return (
    <section className="flex flex-col gap-3 rounded-lg border bg-card p-4">
      <h2 className="text-base font-semibold">{t("info.title")}</h2>
      <dl className="grid grid-cols-1 gap-x-12 gap-y-3 text-sm sm:grid-cols-2">
        <InfoRow
          label={t("info.email")}
          value={user?.email ?? ""}
          editing={editingField === "email"}
          draft={draft}
          onDraftChange={setDraft}
          onEdit={() => startEdit("email")}
          onSave={save}
          onCancel={() => setEditingField(null)}
          saving={mutation.isPending}
        />
        <InfoRow
          label={t("info.name")}
          value={user?.name ?? "—"}
          editing={editingField === "name"}
          draft={draft}
          onDraftChange={setDraft}
          onEdit={() => startEdit("name")}
          onSave={save}
          onCancel={() => setEditingField(null)}
          saving={mutation.isPending}
        />
        <div className="flex items-center gap-2">
          <dt className="text-muted-foreground">{t("info.isMainAccount")}:</dt>
          <dd>{user?.role === "admin" ? t("subAccounts.roles.admin") : t("info.yes")}</dd>
        </div>
        <div className="flex items-center gap-2">
          <dt className="text-muted-foreground">{t("info.workspace")}:</dt>
          <dd>{user?.workspaces?.[0]?.name ?? "—"}</dd>
        </div>
      </dl>
    </section>
  );
}

function InfoRow({
  label,
  value,
  editing,
  draft,
  onDraftChange,
  onEdit,
  onSave,
  onCancel,
  saving,
}: {
  label: string;
  value: string;
  editing: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation("account");
  return (
    <div className="flex items-center gap-2">
      <dt className="shrink-0 text-muted-foreground">{label}:</dt>
      {editing ? (
        <dd className="flex flex-1 items-center gap-2">
          <Input
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            className="h-8"
            autoFocus
          />
          <Button size="sm" onClick={onSave} disabled={saving}>
            {t("info.save")}
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            {t("info.cancel")}
          </Button>
        </dd>
      ) : (
        <dd className="flex items-center gap-1.5">
          <span>{value}</span>
          <button
            type="button"
            aria-label={`${t("info.edit")} ${label}`}
            className="text-muted-foreground hover:text-foreground"
            onClick={onEdit}
          >
            <IconEdit className="size-3.5" />
          </button>
        </dd>
      )}
    </div>
  );
}

function SubAccountTable({
  subAccounts,
  onEdit,
}: {
  subAccounts: SubAccount[];
  onEdit: (subAccount: SubAccount) => void;
}) {
  const { t, i18n } = useTranslation("account");
  const queryClient = useQueryClient();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => deleteSubAccount(userId),
    onSuccess: async () => {
      toast.success(t("subAccounts.deleted"));
      setConfirmingId(null);
      await queryClient.invalidateQueries({ queryKey: SUB_ACCOUNTS_QUERY_KEY });
    },
    onError: async (error) => {
      toast.error(await getApiErrorMessage(error));
    },
  });

  function formatDate(value: string | null): string {
    if (!value) return t("subAccounts.never");
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("subAccounts.columns.name")}</TableHead>
          <TableHead>{t("subAccounts.columns.email")}</TableHead>
          <TableHead>{t("subAccounts.columns.role")}</TableHead>
          <TableHead className="text-right">{t("subAccounts.columns.shops")}</TableHead>
          <TableHead>{t("subAccounts.columns.lastLogin")}</TableHead>
          <TableHead>{t("subAccounts.columns.createdAt")}</TableHead>
          <TableHead className="text-right">{t("subAccounts.columns.actions")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {subAccounts.map((subAccount) => (
          <TableRow key={subAccount.userId}>
            <TableCell>{subAccount.name ?? "—"}</TableCell>
            <TableCell>{subAccount.email}</TableCell>
            <TableCell>
              <Badge variant={subAccount.role === "admin" ? "info" : "neutral"}>
                {t(`subAccounts.roles.${subAccount.role}`)}
              </Badge>
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums">
              {subAccount.shopScope === null || subAccount.shopScope.mode === "all"
                ? t("subAccounts.allShops")
                : subAccount.shopCount}
            </TableCell>
            <TableCell>{formatDate(subAccount.lastLoginAt)}</TableCell>
            <TableCell>{formatDate(subAccount.createdAt)}</TableCell>
            <TableCell>
              {subAccount.role === "admin" ? (
                // 管理员仅在运营后台管理,前台不提供编辑/删除。
                <div className="flex justify-end pr-2 text-muted-foreground">—</div>
              ) : (
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("subAccounts.edit")}
                    onClick={() => onEdit(subAccount)}
                  >
                    <IconEdit />
                  </Button>
                  <Popover
                    open={confirmingId === subAccount.userId}
                    onOpenChange={(open) => setConfirmingId(open ? subAccount.userId : null)}
                  >
                    <PopoverTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t("subAccounts.delete")}
                        />
                      }
                    >
                      <IconTrash />
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-auto p-3">
                      <p className="pb-2 text-sm">{t("subAccounts.deleteConfirm")}</p>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setConfirmingId(null)}>
                          {t("subAccounts.deleteConfirmNo")}
                        </Button>
                        <Button
                          size="sm"
                          disabled={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(subAccount.userId)}
                        >
                          {t("subAccounts.deleteConfirmYes")}
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
